'use client';

import { useState, type CSSProperties, type ImgHTMLAttributes } from 'react';

/**
 * Lightweight image component that owns its resize URL convention.
 *
 * Why not next/image: next/image's value prop is server-side resize via
 * /_next/image (which costs money on Vercel) plus responsive plumbing
 * around it. Since we run CloudFront + Lambda@Edge that does the same
 * resize on its own (see `infra/aws/lambda-edge-resize/`), next/image is
 * just an indirection — we'd rather pass `?w=&format=webp&q=` directly to
 * our origin.
 *
 * What this gives you:
 *  - Auto srcset for retina (1x / 2x / 3x) via the resize endpoint.
 *  - Native lazy loading by default; `priority` opts into eager + high
 *    fetchpriority.
 *  - `aspect-ratio` style so the box reserves space and CLS stays put.
 *  - `fill` mode for parent-sized images.
 *  - Pass-through for relative paths (e.g. /squash-ball.svg in public/),
 *    blob URLs, data URIs, and known static CDNs — no resize attempted.
 */

// Width steps for srcset. Width descriptors (e.g. `?w=256 256w`) play
// correctly with the `sizes` prop — the browser picks the entry closest to
// the *rendered* size × DPR. Density descriptors (1x/2x/3x) would ignore
// `sizes` and force the largest entry whenever DPR > 1.
//
// Policy: always oversample. When the caller asks for `width={128}` we
// don't actually request a 128 px image — we request 2×/3×/4× that. On
// a non-retina display the browser picks 2× and downsamples (cheap, sharp);
// on a 3× retina display it picks 3× and the avatar still looks crisp.
// This avoids the "pixelated avatar at DPR 2" trap where a 128 px source
// gets stretched to a 128 CSS px box that's actually 256 device pixels wide.
const OVERSAMPLE = 2;
const FILL_WIDTHS = [128, 256, 384, 640, 1080, 1920] as const;
const DEFAULT_QUALITY = 90;

export interface ImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'width' | 'height' | 'loading'> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  /** Stretch to fill the nearest positioned ancestor (parent must be position:relative). */
  fill?: boolean;
  /** Above-the-fold: eager loading + fetchpriority="high". */
  priority?: boolean;
  /** Skip the resize query params; serve the original. */
  unoptimized?: boolean;
  /** WebP/JPEG quality 1–100. Defaults to 90. */
  quality?: number;
  className?: string;
  style?: CSSProperties;
}

// Hosts whose URLs we route through the resize pipeline:
//   - Prod CloudFront → resize happens at Lambda@Edge
//   - Local floci on `localhost:4566` or `127.0.0.1:4566` → routed via
//     /api/img/<key> which runs Sharp server-side (mirrors the prod Lambda)
// Anything else passes through untouched.
const LOCAL_FLOCI_HOST = /(?:localhost|127\.0\.0\.1):4566/;
const RESIZABLE_HOST = new RegExp(
  `^https?://(?:assets\\.playpulse\\.io|${LOCAL_FLOCI_HOST.source})\\b`,
);

export function isResizable(src: string): boolean {
  if (!src) return false;
  if (src.startsWith('/')) return false; // local public/ asset
  if (src.startsWith('data:') || src.startsWith('blob:')) return false;
  return RESIZABLE_HOST.test(src);
}

// Local floci has no CloudFront / Lambda@Edge in front of it, so a raw
// `:4566/playpulse-assets/key?w=...` URL would just serve the original.
// Rewrite to `/api/img/<key>` — the same-origin Next.js route fetches via
// the SDK and runs Sharp. Bucket is implicit (always assets), so we strip
// it. Prod URLs (assets.playpulse.io) keep their shape: CloudFront resizes
// them.
const LOCAL_FLOCI_URL = new RegExp(`^http://${LOCAL_FLOCI_HOST.source}/[^/?#]+/([^?#]+)(\\?.*)?$`);

export function rewriteForLocal(src: string): string {
  const m = LOCAL_FLOCI_URL.exec(src);
  if (!m) return src;
  const [, key, query = ''] = m;
  return `/api/img/${key}${query}`;
}

export function withResize(src: string, width: number, quality = DEFAULT_QUALITY): string {
  if (!isResizable(src)) return src;
  const sep = src.includes('?') ? '&' : '?';
  return `${src}${sep}w=${width}&format=webp&q=${quality}`;
}

export function widthSteps(width: number | undefined, fill: boolean | undefined): number[] {
  // Explicit width: always oversample so retina displays stay crisp. Smallest
  // entry is 2×; covers DPR-1 (downsamples cleanly), DPR-2 (exact), DPR-3
  // (3× entry kicks in). Browser picks via the caller's `sizes` prop.
  if (width) return [width * OVERSAMPLE, width * 3, width * 4];
  if (fill) return [...FILL_WIDTHS];
  return [1080];
}

export default function Image({
  src,
  alt,
  width,
  height,
  fill,
  priority,
  unoptimized,
  quality,
  className,
  style,
  onError,
  sizes,
  ...rest
}: ImageProps) {
  // Default `sizes` from `width` when not explicit. Without this, browsers
  // default `sizes` to `100vw` and pick the largest srcset entry — which
  // undoes the whole point of width-descriptor srcset for non-fluid images.
  // With `sizes="{width}px"` the browser picks the smallest entry that
  // satisfies `width × DPR`.
  const resolvedSizes = sizes ?? (width && !fill ? `${width}px` : undefined);
  const [errored, setErrored] = useState(false);
  if (errored || !src) return null;

  const skipResize = unoptimized || !isResizable(src);
  const steps = widthSteps(width, fill);
  // Fallback src (when srcset/sizes negotiation isn't honored): bias
  // upward for the same crispness reason as the srcset itself.
  const baseWidth = width ? width * OVERSAMPLE : steps[Math.floor(steps.length / 2)];

  const finalSrc = skipResize ? src : rewriteForLocal(withResize(src, baseWidth, quality));
  // Width descriptors (`?w=128 128w`) so the browser uses the caller's
  // `sizes` prop to pick the closest entry × DPR.
  const srcSet = skipResize
    ? undefined
    : steps.map((w) => `${rewriteForLocal(withResize(src, w, quality))} ${w}w`).join(', ');

  const aspectStyle: CSSProperties =
    width && height && !fill ? { aspectRatio: `${width} / ${height}` } : {};
  const fillStyle: CSSProperties = fill
    ? { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }
    : {};

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      {...rest}
      src={finalSrc}
      srcSet={srcSet}
      sizes={resolvedSizes}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      onError={(e) => {
        // Caller's onError (if any) runs first — gives them a chance to
        // mutate the DOM / log / fall back BEFORE the component hides
        // itself by re-rendering as null. PlayerAvatar relies on this to
        // replace the broken image with initials.
        onError?.(e);
        setErrored(true);
      }}
      className={className}
      style={{ ...aspectStyle, ...fillStyle, ...style }}
    />
  );
}
